import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Star,
  Package,
  Phone,
  Calendar,
  MapPin,
  Building,
  Grid,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  organizationsAPI,
  membershipPackagesAPI,
  sessionTypesAPI,
} from "../services/api";
import MembershipPackageModal from "../components/ui/MembershipPackageModal";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Skeleton, { TableSkeleton } from "../components/ui/Skeleton";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import SessionTypeModal from "../components/ui/SessionTypeModal";
import { useTheme } from "../contexts/ThemeContext";
import PageHeader from "../components/common/PageHeader";

const OrganizationDetailsPage = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { colors, theme } = useTheme();
  const { t } = useTranslation();

  const [organization, setOrganization] = useState(null);
  const [packages, setPackages] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [selectedSessionTypeId, setSelectedSessionTypeId] = useState("all");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("packages");
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [isSessionTypeModalOpen, setIsSessionTypeModalOpen] = useState(false);
  const [editingSessionType, setEditingSessionType] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    type: null, // "package" or "sessionType"
    item: null,
  });
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [initialSessionTypeId, setInitialSessionTypeId] = useState("");
  const [initialSessionTypeName, setInitialSessionTypeName] = useState("");

  // Fetch organization and packages
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const orgResponse = await organizationsAPI.getById(organizationId);
        setOrganization(orgResponse.data.data);

        const packagesResponse =
          await membershipPackagesAPI.getAllByOrg(organizationId);
        setPackages(packagesResponse.data.data || []);

        const typesResponse = await sessionTypesAPI.getAllByOrg(organizationId);
        setSessionTypes(typesResponse.data.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load organization details");
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchData();
    }
  }, [organizationId]);

  const handleCreatePackage = async (packageData) => {
    try {
      setActionLoading(true);
      const response = await membershipPackagesAPI.create(
        organizationId,
        packageData,
      );
      setPackages((prev) => [...prev, response.data.data]);
      setIsPackageModalOpen(false);
      setError(null);
    } catch (err) {
      console.error("Error creating package:", err);
      setError("Failed to create package");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePackage = async (packageData) => {
    try {
      setActionLoading(true);
      const response = await membershipPackagesAPI.update(
        organizationId,
        editingPackage.id,
        packageData,
      );
      setPackages((prev) =>
        prev.map((pkg) =>
          pkg.id === editingPackage.id ? response.data.data : pkg,
        ),
      );
      setIsPackageModalOpen(false);
      setEditingPackage(null);
      setError(null);
    } catch (err) {
      console.error("Error updating package:", err);
      setError("Failed to update package");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteItem = async () => {
    try {
      setActionLoading(true);
      if (deleteConfirm.type === "package") {
        await membershipPackagesAPI.delete(
          organizationId,
          deleteConfirm.item.id,
        );
        setPackages((prev) =>
          prev.filter((pkg) => pkg.id !== deleteConfirm.item.id),
        );
      } else {
        await sessionTypesAPI.delete(organizationId, deleteConfirm.item.id);
        setSessionTypes((prev) =>
          prev.filter((st) => st.id !== deleteConfirm.item.id),
        );
      }
      setDeleteConfirm({ isOpen: false, type: null, item: null });
      setError(null);
    } catch (err) {
      console.error(`Error deleting ${deleteConfirm.type}:`, err);
      setError(`Failed to delete ${deleteConfirm.type}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateSessionType = async (typeData) => {
    try {
      setActionLoading(true);
      const response = await sessionTypesAPI.create(organizationId, typeData);
      setSessionTypes((prev) => [...prev, response.data.data]);
      setIsSessionTypeModalOpen(false);
      setError(null);
    } catch (err) {
      console.error("Error creating session type:", err);
      setError("Failed to create session type");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateSessionType = async (typeData) => {
    try {
      setActionLoading(true);
      const response = await sessionTypesAPI.update(
        organizationId,
        editingSessionType.id,
        typeData,
      );
      setSessionTypes((prev) =>
        prev.map((st) =>
          st.id === editingSessionType.id ? response.data.data : st,
        ),
      );
      setIsSessionTypeModalOpen(false);
      setEditingSessionType(null);
      setError(null);
    } catch (err) {
      console.error("Error updating session type:", err);
      setError("Failed to update session type");
    } finally {
      setActionLoading(false);
    }
  };

  const openCreatePackageModal = (sessionTypeId = "", sessionTypeName = "") => {
    setInitialSessionTypeId(sessionTypeId);
    setInitialSessionTypeName(sessionTypeName);
    setEditingPackage(null);
    setIsPackageModalOpen(true);
  };

  const openEditPackageModal = (package_) => {
    setInitialSessionTypeId(package_.sessionTypeId || "");
    setInitialSessionTypeName(package_.sessionTypeName || "");
    setEditingPackage(package_);
    setIsPackageModalOpen(true);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openCreateSessionTypeModal = () => {
    setEditingSessionType(null);
    setIsSessionTypeModalOpen(true);
  };

  const openEditSessionTypeModal = (type) => {
    setEditingSessionType(type);
    setIsSessionTypeModalOpen(true);
  };

  const openDeleteConfirm = (type, item) => {
    setDeleteConfirm({ isOpen: true, type, item });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
    }).format(price);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700 ring-1 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/30";
      case "inactive":
        return "bg-gray-50 text-gray-700 ring-1 ring-gray-600/20 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-500/30";
      case "ongoing":
        return "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-500/30";
      case "completed":
        return "bg-purple-50 text-purple-700 ring-1 ring-purple-600/20 dark:bg-purple-900/30 dark:text-purple-400 dark:ring-purple-500/30";
      case "suspended":
        return "bg-red-50 text-red-700 ring-1 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/30";
      default:
        return "bg-gray-50 text-gray-700 ring-1 ring-gray-600/20 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-500/30";
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${colors.bgSecondary} p-4 md:p-8 space-y-8 animate-in fade-in duration-500`}>
        {/* Org Header Skeleton */}
        <div className={`p-6 rounded-2xl ${colors.bgPrimary} border ${colors.cardBorder} shadow-xl flex items-center gap-6`}>
          <Skeleton variant="rectangle" width="80px" height="20" />
          <div className="space-y-4 flex-1">
            <Skeleton width="300px" height="8" />
            <div className="flex gap-8">
              <Skeleton width="150px" height="4" className="opacity-60" />
              <Skeleton width="150px" height="4" className="opacity-60" />
              <Skeleton width="150px" height="4" className="opacity-60" />
            </div>
          </div>
        </div>

        {/* Content Tabs Skeleton */}
        <div className={`p-8 rounded-2xl ${colors.bgPrimary} border ${colors.cardBorder} shadow-xl space-y-8`}>
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <Skeleton width="200px" height="6" />
              <Skeleton width="320px" height="4" className="opacity-40" />
            </div>
            <Skeleton width="160px" height="12" />
          </div>

          <TableSkeleton rows={6} columns={5} />
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div
        className={`flex h-full ${colors.bgPrimary} p-6 items-center justify-center`}
      >
        <div className="text-center">
          <p className={colors.textMuted}>
            {t("organizationDetailsPage.notFound")}
          </p>
          <button
            onClick={() => navigate("/organizations")}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
          >
            {t("organizationDetailsPage.backToOrganizations")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`px-4 pb-8`}>
      <div
        className={`
        flex flex-col
        ${colors.cardBg}
        ${colors.cardBorder} border rounded-2xl shadow-xl
        relative transition-all duration-500
      `}
      >
        {/* Compact Integrated Header Area */}
        <div
          className={`p-4 md:px-6 md:py-3 border-b ${colors.tableBorder} ${colors.tableHeader} flex flex-wrap lg:flex-nowrap items-center justify-between gap-4 rounded-t-2xl`}
        >
          {/* Left: Back & Title */}
          <div className="flex items-center space-x-3 shrink-0 mr-4">
            <button
              onClick={() => navigate("/organizations")}
              className={`p-2 rounded-xl transition-all duration-300 ${colors.bgHover} hover:scale-105 active:scale-95 border border-transparent hover:border-gray-200 dark:hover:border-white/10`}
            >
              <ArrowLeft
                className={`w-4 h-4 sm:w-5 sm:h-5 ${colors.textSecondary} group-hover:text-primary transition-colors`}
              />
            </button>

            {organization.logo ? (
              <img
                src={organization.logo}
                alt={organization.name}
                className="w-10 h-10 object-contain rounded-xl border border-slate-100 dark:border-white/10 bg-white p-1"
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg text-white">
                <Building className="w-5 h-5" />
              </div>
            )}

            <div className="flex flex-col">
              <h1
                className={`text-lg sm:text-xl font-bold ${colors.textPrimary} tracking-tight leading-tight`}
              >
                {organization.name}
              </h1>
              <p
                className={`text-[10px] sm:text-xs ${colors.textTertiary} font-medium leading-none mt-0.5`}
              >
                {t("organizationDetailsPage.management")}
              </p>
            </div>
          </div>

          {/* Middle: Compact Info Strip */}
          <div className="hidden md:flex flex-1 items-center gap-6 px-4 border-x border-gray-200 dark:border-white/10 mx-2 overflow-x-auto whitespace-nowrap hide-scrollbar">
            {/* Address */}
            <div className="flex flex-col justify-center">
              <span
                className={`text-[9px] font-bold uppercase tracking-wider ${colors.textTertiary} leading-none mb-1`}
              >
                {t("organizationDetailsPage.address")}
              </span>
              <span
                className={`text-sm font-bold ${colors.textPrimary} leading-none truncate max-w-[200px]`}
              >
                {organization.address}
              </span>
            </div>

            {/* Phone */}
            <div className="flex flex-col justify-center">
              <span
                className={`text-[9px] font-bold uppercase tracking-wider ${colors.textTertiary} leading-none mb-1`}
              >
                {t("organizationDetailsPage.phone")}
              </span>
              <span
                className={`text-sm font-bold ${colors.textPrimary} leading-none`}
              >
                {organization.phone}
              </span>
            </div>

            {/* Created */}
            <div className="flex flex-col justify-center">
              <span
                className={`text-[9px] font-bold uppercase tracking-wider ${colors.textTertiary} leading-none mb-1`}
              >
                {t("organizationDetailsPage.created")}
              </span>
              <span
                className={`text-sm font-bold ${colors.textPrimary} leading-none`}
              >
                {new Date(organization.createdAt).toLocaleDateString()}
              </span>
            </div>

            {/* Total Packages */}
            <div className="flex flex-col justify-center">
              <span
                className={`text-[9px] font-bold uppercase tracking-wider ${colors.textTertiary} leading-none mb-1`}
              >
                {t("organizationDetailsPage.packages")}
              </span>
              <span
                className={`flex items-center gap-1 text-sm font-bold text-blue-600 dark:text-blue-400 leading-none`}
              >
                <Grid className="w-3.5 h-3.5" />
                {packages.length} {t("organizationDetailsPage.totalActive")}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 md:p-8 pb-12">

          {/* Content Area */}
          {activeTab === "packages" && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                <div>
                  <h2 className={`text-xl font-bold ${colors.textPrimary}`}>
                    {t("organizationDetailsPage.tabs.membershipPackages")}
                  </h2>
                  <p className={`text-sm ${colors.textTertiary} mt-1`}>
                    {t("organizationDetailsPage.packagesSection.managePackagesDesc")}
                  </p>
                </div>
                <button
                  onClick={openCreateSessionTypeModal}
                  className="w-full sm:w-auto justify-center flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all shadow-lg hover:shadow-blue-500/30 transform hover:scale-105 active:scale-95 text-sm font-bold"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t("sessionTypeModal.buttons.create")}</span>
                </button>
              </div>

              <div
                className={`overflow-x-auto border ${colors.cardBorder} rounded-xl shadow-sm bg-white dark:bg-black/20`}
              >
                <table className="w-full text-left border-collapse whitespace-nowrap min-w-[800px]">
                  <thead>
                    <tr
                      className={`${colors.tableHeader} border-b ${colors.tableBorder}`}
                    >
                      <th
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${colors.textTertiary} w-10`}
                      ></th>
                      <th
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${colors.textTertiary}`}
                      >
                        {t("sessionTypeModal.labels.name")}
                      </th>
                      <th
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${colors.textTertiary}`}
                      >
                        {t("sessionTypeModal.labels.category")}
                      </th>
                      <th
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${colors.textTertiary} text-center`}
                      >
                        {t("sessionTypeModal.labels.maxParticipants")}
                      </th>
                      <th
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${colors.textTertiary} text-center`}
                      >
                        {t("sessionTypeModal.labels.minEvents")}
                      </th>
                      <th
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${colors.textTertiary} text-center`}
                      >
                        {t("sessionTypeModal.labels.status")}
                      </th>
                      <th
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${colors.textTertiary} text-center`}
                      >
                        {t("organizationDetailsPage.packages")}
                      </th>
                      <th
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${colors.textTertiary} text-right`}
                      >
                        {t("common.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${colors.tableBorder}`}>
                    {sessionTypes.map((type) => {
                      const isExpanded = expandedRows[type.id];
                      const typePackages = packages.filter(
                        (p) => p.sessionTypeId === type.id,
                      );

                      return (
                        <React.Fragment key={type.id}>
                          <tr
                            onClick={() => toggleRow(type.id)}
                            className={`group cursor-pointer transition-colors ${isExpanded
                                ? "bg-blue-50/30 dark:bg-blue-900/10"
                                : "hover:bg-gray-50 dark:hover:bg-white/5"
                              }`}
                          >
                            <td className="px-6 py-4 text-center">
                              {isExpanded ? (
                                <ChevronUp
                                  className={`w-5 h-5 ${colors.textTertiary}`}
                                />
                              ) : (
                                <ChevronDown
                                  className={`w-5 h-5 ${colors.textTertiary}`}
                                />
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`font-bold ${colors.textPrimary}`}
                              >
                                {type.name}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${type.maxParticipants > 1
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                    : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                                  }`}
                              >
                                {type.maxParticipants > 1
                                  ? t("sessionTypeModal.options.fixedGroup")
                                  : t("sessionTypeModal.options.individual")}
                              </span>
                            </td>
                            <td className={`px-6 py-4 text-center text-sm font-medium ${colors.textSecondary}`}>
                              {type.maxParticipants}
                            </td>
                            <td className={`px-6 py-4 text-center text-sm font-medium ${colors.textSecondary}`}>
                              0
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span
                                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider ${type.status === "active"
                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                    : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
                                  }`}
                              >
                                {type.status === "active"
                                  ? t("membershipPackageModal.options.active")
                                  : t("membershipPackageModal.options.inactive")}
                              </span>
                            </td>
                            <td className={`px-6 py-4 text-center text-sm font-bold ${colors.textSecondary}`}>
                              {typePackages.length}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditSessionTypeModal(type);
                                  }}
                                  className="p-2 text-blue-500 hover:bg-blue-500/10 rounded-lg transition-colors border border-gray-100 dark:border-white/5"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteConfirm("sessionType", type);
                                  }}
                                  className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors border border-gray-100 dark:border-white/5"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr className="bg-gray-50/50 dark:bg-white/5 animate-in slide-in-from-top-1 duration-200 shadow-inner">
                              <td colSpan="8" className="p-8">
                                <div className="flex flex-col space-y-6">
                                  {/* Header inside accordion */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400">
                                        <Package className="w-5 h-5" />
                                      </div>
                                      <div>
                                        <h4
                                          className={`text-lg font-bold ${colors.textPrimary}`}
                                        >
                                          {t("organizationDetailsPage.packagesSection.packagesFor", { name: type.name })}
                                        </h4>
                                        <p
                                          className={`text-sm ${colors.textTertiary}`}
                                        >
                                          {typePackages.length} {t("organizationDetailsPage.packagesSection.availablePackages")}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() =>
                                        openCreatePackageModal(
                                          type.id,
                                          type.name,
                                        )
                                      }
                                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl transition-all shadow-lg hover:shadow-emerald-500/30 transform hover:scale-105 active:scale-95 text-xs font-bold"
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                      <span>{t("organizationDetailsPage.packagesSection.createPackage")}</span>
                                    </button>
                                  </div>

                                  {/* Packages Grid */}
                                  {typePackages.length === 0 ? (
                                    <div className="text-center py-10 rounded-2xl border border-dashed border-gray-300 dark:border-white/10">
                                      <p className={colors.textTertiary}>
                                        Bu oturum türü için henüz paket
                                        oluşturulmadı.
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                      {typePackages.map((pkg) => (
                                        <div
                                          key={pkg.id}
                                          className={`
                                            group relative flex flex-col
                                            ${theme === "dark"
                                              ? "bg-[#18181b]"
                                              : "bg-white"
                                            } 
                                            border ${theme === "dark"
                                              ? "border-[#27272a]"
                                              : "border-slate-100"
                                            } 
                                            rounded-xl p-5
                                            shadow-sm hover:shadow-xl 
                                            transition-all duration-300 transform hover:-translate-y-1
                                          `}
                                        >
                                          <div className="flex justify-between items-start mb-4">
                                            <div className="flex-1 mr-4">
                                              <div className="flex items-center gap-2 mb-2">
                                                <h3
                                                  className={`text-lg font-bold ${colors.textPrimary} leading-tight`}
                                                >
                                                  {pkg.name}
                                                </h3>
                                              </div>
                                              <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${getStatusBadgeColor(
                                                  pkg.status,
                                                )}`}
                                              >
                                                {t(
                                                  `organizationDetailsPage.packagesSection.statuses.${pkg.status}`,
                                                )}
                                              </span>
                                            </div>
                                            <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <button
                                                onClick={() =>
                                                  openEditPackageModal(pkg)
                                                }
                                                className="p-1.5 text-blue-500 hover:bg-blue-500/10 rounded-lg transition-colors border border-gray-100 dark:border-white/5 bg-white dark:bg-black/20"
                                              >
                                                <Edit className="w-3.5 h-3.5" />
                                              </button>
                                              <button
                                                onClick={() =>
                                                  openDeleteConfirm(
                                                    "package",
                                                    pkg,
                                                  )
                                                }
                                                className="p-1.5 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors border border-gray-100 dark:border-white/5 bg-white dark:bg-black/20"
                                              >
                                                <Trash2 className="w-3.5 h-3.5" />
                                              </button>
                                            </div>
                                          </div>

                                          <div className="space-y-3 mb-4">
                                            <div className="flex items-end baseline">
                                              <p
                                                className={`text-2xl font-black ${colors.textPrimary} tracking-tight`}
                                              >
                                                {formatPrice(pkg.price)}
                                              </p>
                                              <p
                                                className={`text-[10px] ${colors.textTertiary} mb-1 ml-1 font-medium`}
                                              >
                                                / {pkg.numberOfSessions}{" "}
                                                seans
                                              </p>
                                            </div>

                                            <div
                                              className={`grid grid-cols-2 gap-2 p-2.5 rounded-lg ${theme === "dark"
                                                  ? "bg-black/20"
                                                  : "bg-slate-50"
                                                }`}
                                            >
                                              <div>
                                                <p
                                                  className={`text-[9px] uppercase font-bold ${colors.textTertiary}`}
                                                >
                                                  GEÇERLİLİK
                                                </p>
                                                <p
                                                  className={`text-xs font-semibold ${colors.textSecondary}`}
                                                >
                                                  {pkg.validityPeriod} GÜN
                                                </p>
                                              </div>
                                              <div>
                                                <p
                                                  className={`text-[9px] uppercase font-bold ${colors.textTertiary}`}
                                                >
                                                  SEANS BAŞI
                                                </p>
                                                <p
                                                  className={`text-xs font-semibold ${colors.textSecondary}`}
                                                >
                                                  {formatPrice(
                                                    pkg.singleSessionPrice,
                                                  )}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>

      <MembershipPackageModal
        isOpen={isPackageModalOpen}
        onClose={() => {
          setIsPackageModalOpen(false);
          setEditingPackage(null);
        }}
        onSubmit={editingPackage ? handleUpdatePackage : handleCreatePackage}
        package={editingPackage}
        title={
          editingPackage
            ? t("organizationDetailsPage.packagesSection.editPackage")
            : t("organizationDetailsPage.packagesSection.createPackageTitle")
        }
        loading={actionLoading}
        organizationId={organizationId}
        initialSessionTypeId={initialSessionTypeId}
        initialSessionTypeName={initialSessionTypeName}
      />

      <SessionTypeModal
        isOpen={isSessionTypeModalOpen}
        onClose={() => {
          setIsSessionTypeModalOpen(false);
          setEditingSessionType(null);
        }}
        onSubmit={
          editingSessionType ? handleUpdateSessionType : handleCreateSessionType
        }
        sessionType={editingSessionType}
        title={
          editingSessionType
            ? "Oturum Tipini Düzenle"
            : "Yeni Oturum Tipi Oluştur"
        }
        loading={actionLoading}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, type: null, item: null })}
        onConfirm={handleDeleteItem}
        title={
          deleteConfirm.type === "package"
            ? t("organizationDetailsPage.packagesSection.deletePackageTitle")
            : "Oturum Tipini Sil"
        }
        message={
          deleteConfirm.type === "package"
            ? t("organizationDetailsPage.packagesSection.deletePackageMessage", {
              name: deleteConfirm.item?.name,
            })
            : `"${deleteConfirm.item?.name}" oturum tipini silmek istediğinizden emin misiniz?`
        }
        confirmText={
          deleteConfirm.type === "package"
            ? t("organizationDetailsPage.packagesSection.deletePackageConfirm")
            : "Sil"
        }
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
      />
    </div>
  );
};

export default OrganizationDetailsPage;
